import { createFileRoute } from "@tanstack/react-router";
import NavBar from "#/components/navbar/navbar";
import Footer from "#/components/footer/footer";
import Mdx from "#/components/MdxRemote";
import { metadataToHead, type PageMetadata } from "#/lib/seo";

export default function Page() {
  return (
    <>
      <header>
        <NavBar />
      </header>

      <main id="content" className="mx-auto mt-10 max-w-3xl space-y-10">
        <article>
          <h1 className="mb-10 px-5 text-center text-5xl">
            Nurse Salary in the United States
          </h1>

          <section className="mx-8 space-y-5">
            <p>
              The average nurse salary in the United States is{" "}
              <strong>$89,000</strong> and the range is usually between{" "}
              <strong>$55,000</strong> and <strong>$125,000</strong>.
            </p>

            <p>
              Nurses are in high demand across the United States. This not only
              makes their career fulfilling, but also a financially rewarding
              choice for many.
            </p>
          </section>

          <section className="mx-8 space-y-5">
            <h2>How much nurses make by specialty</h2>

            <Mdx source={content} />
          </section>
        </article>
      </main>
      <Footer fmBio />
    </>
  );
}

export const metadata: PageMetadata = {
  title: `Salary for Nurses in the United States`,
  description:
    "The average nurse salary in the United States is $89,000 and the range is usually between $55,000 and $125,000.",
  openGraph: {
    images: ["https://roamdoc.com/og-image.png"],
  },
  alternates: {
    canonical: `https://www.roamdoc.com/nurse-salary-in-united-states`,
  },
};

const content = `
Nursing is a diverse field with various specialties, each having its own unique set of responsibilities and required skills. Consequently, the salaries for nurses can vary significantly depending on their specialty. Below, we explore some of the common nursing specialties and provide an overview of what nurses in these fields can expect to earn on average in the United States.

### Registered Nurse (RN)
Registered Nurses are the backbone of the healthcare industry, providing critical care to patients in various settings. The average salary for RNs varies widely depending on experience, education, and geographical location, but the national average salary is typically around $75,000 per year.

### Nurse Practitioner (NP)
Nurse Practitioners are advanced practice registered nurses who provide high-quality healthcare services similar to those of a physician. NPs can diagnose conditions, prescribe medications, and initiate treatment plans. The average salary for a Nurse Practitioner in the U.S. is around $110,000 per year, although this can fluctuate based on specialization, location, and experience.

### Clinical Nurse Specialist (CNS)
Clinical Nurse Specialists are advanced practice registered nurses who focus on improving the quality of nursing care and patient outcomes. They specialize in a specific area of nursing, such as geriatrics, pediatrics, or emergency care. The average annual salary for CNS roles is around $100,000, but this can vary depending on the area of specialization.

### Certified Registered Nurse Anesthetist (CRNA)
CRNAs are among the highest-paid nursing professionals. They work in collaboration with surgeons, anesthesiologists, and other healthcare professionals to deliver anesthesia and pain management services. The average salary for a CRNA in the United States is around $180,000 per year, although this can vary based on experience and location.

### Nurse Midwife
Nurse Midwives provide care to women, including gynecological exams, family planning services, prenatal care, childbirth, and postpartum care. The average salary for Nurse Midwives in the U.S. is around $108,000 per year, but this can vary depending on the work setting and geographical location.

### Pediatric Nurse
Pediatric Nurses specialize in caring for children, from infants to adolescents. They work closely with pediatricians to provide care and support for various medical needs. The average salary for a Pediatric Nurse in the United States is around $71,000 per year, but this can vary based on experience and location.

### Oncology Nurse
Oncology Nurses specialize in providing care for cancer patients. They administer treatments, manage symptoms, and provide emotional support to patients and their families. The average salary for an Oncology Nurse in the U.S. is around $73,000 per year, but this can vary depending on the level of experience and the work setting.

### Geriatric Nurse
Geriatric Nurses specialize in caring for elderly patients. They address the unique health needs of the aging population, including chronic illnesses, mobility issues, and mental health concerns. The average salary for a Geriatric Nurse in the United States is approximately $70,000 per year, although this can vary based on the nurse's experience and the healthcare setting.
`;

export const Route = createFileRoute("/(salary)/nurse-salary-in-united-states/")({
  component: Page,
  head: () => metadataToHead(metadata),
});
